import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Select,
    Typography,
    Tag,
    Space,
    Spin,
    Grid,
    Card,
    Descriptions,
    Divider,
    message,
} from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import orderService from '../../services/orderService';
import ErrorMessage from '../common/ErrorMessage';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const ORDER_STATUSES = [
    { value: 'pending', label: 'Kutilmoqda', color: 'gold' },
    { value: 'confirmed', label: 'Tasdiqlangan', color: 'blue' },
    { value: 'processing', label: 'Jarayonda', color: 'cyan' },
    { value: 'shipped', label: 'Yetkazilmoqda', color: 'purple' },
    { value: 'delivered', label: 'Yetkazildi', color: 'green' },
    { value: 'cancelled', label: 'Bekor qilindi', color: 'red' },
];

const AdminOrderManager = () => {
    const screens = useBreakpoint();
    const [orders, setOrders] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);
    const [form] = Form.useForm();

    const fetchOrders = async (page = 1, limit = 10, status = null) => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit };
            if (status) params.status = status;
            const response = await orderService.getAll(params);
            setOrders(response.orders || []);
            setPagination(response.pagination || { total: 0, page: 1, limit: 10 });
        } catch (err) {
            setError(err.response?.data?.message || 'Buyurtmalarni yuklashda xatolik');
            console.error('Failed to fetch orders:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(pagination.page, pagination.limit, statusFilter);
    }, []);

    const handleTableChange = (pag) => {
        fetchOrders(pag.current, pag.pageSize, statusFilter);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        fetchOrders(1, pagination.limit, value);
    };

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setViewModalOpen(true);
    };

    const handleEditOrder = (order) => {
        setSelectedOrder(order);
        form.setFieldsValue({ status: order.status });
        setEditModalOpen(true);
    };

    const handleUpdateStatus = async (values) => {
        try {
            await orderService.updateStatus(selectedOrder.id, values.status);
            message.success('Buyurtma holati yangilandi');
            setEditModalOpen(false);
            fetchOrders(pagination.page, pagination.limit, statusFilter);
        } catch (err) {
            message.error(err.response?.data?.message || 'Xatolik yuz berdi');
        }
    };

    const getStatusTag = (status) => {
        const statusInfo = ORDER_STATUSES.find((s) => s.value === status);
        return statusInfo ? (
            <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
        ) : (
            <Tag>{status}</Tag>
        );
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: 'Mijoz',
            key: 'customer',
            render: (_, record) => (
                <div>
                    <Text strong>
                        {record.customer?.first_name} {record.customer?.last_name}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.customer?.phone_number}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Sotuvchi',
            key: 'seller',
            render: (_, record) =>
                record.seller ? (
                    `${record.seller.first_name} ${record.seller.last_name}`
                ) : (
                    <Text type="secondary">Tayinlanmagan</Text>
                ),
            responsive: ['md'],
        },
        {
            title: 'Hudud',
            key: 'region',
            render: (_, record) => record.region?.name || 'N/A',
            responsive: ['sm'],
        },
        {
            title: 'Jami',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount) => `${Number(amount).toLocaleString()} UZS`,
            width: 130,
        },
        {
            title: 'Holat',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status),
            width: 120,
        },
        {
            title: 'Sana',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => new Date(date).toLocaleString('uz-UZ'),
            responsive: ['md'],
            width: 140,
        },
        {
            title: 'Amallar',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewOrder(record)}
                    />
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditOrder(record)}
                    />
                </Space>
            ),
            align: 'right',
            width: 100,
        },
    ];

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: screens.xs ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: screens.xs ? 'stretch' : 'center',
                    gap: 16,
                    marginBottom: 24,
                }}
            >
                <Title level={2} style={{ margin: 0 }}>
                    Buyurtmalar boshqaruvi
                </Title>
                <Select
                    placeholder="Holat bo'yicha filtr"
                    allowClear
                    style={{ width: screens.xs ? '100%' : 200 }}
                    onChange={handleStatusFilterChange}
                    value={statusFilter}
                >
                    {ORDER_STATUSES.map((s) => (
                        <Option key={s.value} value={s.value}>
                            {s.label}
                        </Option>
                    ))}
                </Select>
            </div>

            {error && <ErrorMessage message={error} />}

            <Table
                columns={columns}
                dataSource={orders}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: pagination.total,
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
                scroll={{ x: 800 }}
            />

            {/* View Order Modal */}
            <Modal
                title={`Buyurtma #${selectedOrder?.id}`}
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                footer={null}
                width={700}
            >
                {selectedOrder && (
                    <div>
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label="Sana">
                                {new Date(selectedOrder.createdAt).toLocaleString('uz-UZ')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mijoz">
                                {selectedOrder.customer?.first_name} {selectedOrder.customer?.last_name}
                            </Descriptions.Item>
                            <Descriptions.Item label="Telefon">
                                {selectedOrder.customer?.phone_number}
                            </Descriptions.Item>
                            <Descriptions.Item label="Hudud">
                                {selectedOrder.region?.name}
                            </Descriptions.Item>
                            <Descriptions.Item label="Manzil">
                                {selectedOrder.delivery_address}
                            </Descriptions.Item>
                            <Descriptions.Item label="Holat">
                                {getStatusTag(selectedOrder.status)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Jami summa">
                                {Number(selectedOrder.total_amount).toLocaleString()} UZS
                            </Descriptions.Item>
                            <Descriptions.Item label="Izoh">
                                {selectedOrder.notes || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider>Mahsulotlar</Divider>

                        {selectedOrder.orderItems?.map((item) => (
                            <Card key={item.id} size="small" style={{ marginBottom: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div>
                                        <Text strong>{item.product?.name}</Text>
                                        <br />
                                        <Text type="secondary">
                                            {item.quantity} x {Number(item.price).toLocaleString()} UZS
                                        </Text>
                                    </div>
                                    <Text strong>{Number(item.subtotal).toLocaleString()} UZS</Text>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </Modal>

            {/* Edit Status Modal */}
            <Modal
                title="Buyurtma holatini o'zgartirish"
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                onOk={() => form.submit()}
                okText="Saqlash"
                cancelText="Bekor qilish"
            >
                <Form form={form} layout="vertical" onFinish={handleUpdateStatus}>
                    <Form.Item
                        name="status"
                        label="Yangi holat"
                        rules={[{ required: true, message: 'Holatni tanlang' }]}
                    >
                        <Select>
                            {ORDER_STATUSES.map((s) => (
                                <Option key={s.value} value={s.value}>
                                    {s.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AdminOrderManager;
